import ActionMenu from "./ActionMenu";
import ListingDetailsFields, { listingPayload } from "./ListingDetailsFields";
import "../styles/ListingDetailsFields.css";
import React, { useCallback, useEffect, useState } from "react";
import api from "../api";
import { useNavigate } from "react-router-dom";
import bgVideo from "../assets/lbg.mp4";

const asArray = (payload) =>
  Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload?.items)
    ? payload.items
    : Array.isArray(payload?.results)
    ? payload.results
    : [];

export default function PropertyList() {
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [editingProperty, setEditingProperty] = useState(null);
  const [toast, setToast] = useState(null);
  const [undoPropertyId, setUndoPropertyId] = useState(null);
  const navigate = useNavigate();

  const formatDate = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleDateString();
  };

  const leaseHealthLabel = (value) => {
    if (value === "expired") return "Expired";
    if (value === "expiring_7") return "Expiring 7d";
    if (value === "expiring_15") return "Expiring 15d";
    if (value === "expiring_30") return "Expiring 30d";
    if (value === "no_end_date") return "No End Date";
    return "Active";
  };

  const renewalLabel = (value) => {
    if (!value) return "active";
    return String(value).replaceAll("_", " ");
  };

  const parseImageLines = (value) =>
    String(value || "")
      .split(/\r?\n/)
      .map((item) => item.trim())
      .filter(Boolean);

  const mergeImageValues = (values) =>
    Array.from(new Set(values.map((value) => String(value || "").trim()).filter(Boolean))).slice(0, 12);

  const isDataImage = (value) => /^data:image\//i.test(String(value || ""));

  const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const fetchTenants = async () => {
    try {
      const res = await api.get("/admin/tenants");
      setTenants(res.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchProperties = useCallback(async () => {
    try {
      const res = await api.get("/rental-properties");
      const mapped = asArray(res.data).map((p) => ({
        id: p.id,
        property_name: p.property_name,
        location: p.location,
        rent_price: p.rent_price,
        latitude: p.latitude ?? null,
        longitude: p.longitude ?? null,
        description: p.description,
        listing_details: p.listing_details || {},
        images: Array.isArray(p.images) ? p.images : [],
        isOccupied: Boolean(p.is_occupied),
        tenant: p.tenant || null,
        tenant_id: p.tenant?.id || "",
        lease_start_date: p.lease_start_date?.slice(0, 10) || "",
        lease_end_date: p.lease_end_date?.slice(0, 10) || "",
        notice_date: p.notice_date?.slice(0, 10) || "",
        move_out_date: p.move_out_date?.slice(0, 10) || "",
        renewal_status: p.renewal_status || "active",
        lease_notes: p.lease_notes || "",
        lease_days_remaining:
          p.lease_days_remaining === null || p.lease_days_remaining === undefined
            ? null
            : Number(p.lease_days_remaining),
        lease_health: p.lease_health || "no_end_date",
      }));
      setProperties(mapped);
    } catch (err) {
      console.error(err);
      if (err.response && err.response.status === 401) {
        localStorage.clear();
        navigate("/");
      }
    }
  }, [navigate]);

  useEffect(() => {
    fetchProperties();
    fetchTenants();
  }, [fetchProperties]);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to archive this property?")) return;

    const category = window
      .prompt(
        "Archive category (Resolved, Duplicate, Invalid, Policy, Other)",
        "Resolved"
      )
      ?.trim();
    if (!category) return;

    const allowed = ["Resolved", "Duplicate", "Invalid", "Policy", "Other"];
    if (!allowed.includes(category)) {
      setToast({ message: "Invalid archive category.", type: "error" });
      setTimeout(() => setToast(null), 2500);
      return;
    }

    const note = window.prompt("Archive note", "No longer active listing")?.trim();
    if (!note || note.length < 3) {
      setToast({ message: "Archive note is required.", type: "error" });
      setTimeout(() => setToast(null), 2500);
      return;
    }

    try {
      await api.delete(`/rental-properties/${id}`, {
        data: {
          archive_reason_category: category,
          archive_reason_note: note,
        },
      });
      fetchProperties();
      setUndoPropertyId(id);
      setTimeout(() => setUndoPropertyId(null), 8000);
      setToast({ message: "Property archived. Undo available for 8 seconds.", type: "success" });
      setTimeout(() => setToast(null), 2500);
    } catch (err) {
      console.error(err);
      setToast({ message: "Failed to archive property.", type: "error" });
      setTimeout(() => setToast(null), 2500);
    }
  };

  const handleUndoArchive = async () => {
    if (!undoPropertyId) return;
    try {
      await api.put(`/rental-properties/${undoPropertyId}/restore`);
      setUndoPropertyId(null);
      fetchProperties();
      setToast({ message: "Archive undone. Property restored.", type: "success" });
      setTimeout(() => setToast(null), 2500);
    } catch (err) {
      console.error(err);
      setToast({ message: "Failed to undo archive.", type: "error" });
      setTimeout(() => setToast(null), 2500);
    }
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditingProperty((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
      ...(name === "isOccupied" && !checked ? { tenant_id: "" } : {}),
    }));
  };

  const handleSaveEdit = async () => {
    if (!editingProperty) return;
    try {
      await api.put(`/rental-properties/${editingProperty.id}`, {
        listing_details: listingPayload(editingProperty.listing_details || {}),
        property_name: editingProperty.property_name,
        location: editingProperty.location,
        rent_price: editingProperty.rent_price,
        latitude: editingProperty.latitude,
        longitude: editingProperty.longitude,
        description: editingProperty.description,
        images: mergeImageValues([
          ...(editingProperty.uploaded_images || []),
          ...parseImageLines(editingProperty.image_urls_text),
        ]),
        is_occupied: editingProperty.isOccupied,
        tenant_id: editingProperty.isOccupied ? editingProperty.tenant_id : null,
        lease_start_date: editingProperty.lease_start_date || null,
        lease_end_date: editingProperty.lease_end_date || null,
        notice_date: editingProperty.notice_date || null,
        move_out_date: editingProperty.move_out_date || null,
        renewal_status: editingProperty.renewal_status || "active",
        lease_notes: editingProperty.lease_notes || null,
      });

      setToast({ message: "Property updated.", type: "success" });
      setEditingProperty(null);
      fetchProperties();
      setTimeout(() => setToast(null), 3000);
    } catch (err) {
      console.error(err);
      setToast({ message: err.response?.data?.message || "Failed to update property.", type: "error" });
      setTimeout(() => setToast(null), 3000);
    }
  };

  const handleEditImageUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!editingProperty || files.length === 0) {
      return;
    }

    try {
      const allowed = files.filter((file) => file.type.startsWith("image/"));
      const encoded = await Promise.all(allowed.map((file) => readFileAsDataUrl(file)));
      setEditingProperty((prev) => ({
        ...prev,
        uploaded_images: mergeImageValues([...(prev.uploaded_images || []), ...encoded]),
      }));
    } catch (error) {
      console.error(error);
      setToast({ message: "Failed to read image files.", type: "error" });
      setTimeout(() => setToast(null), 3000);
    } finally {
      event.target.value = "";
    }
  };

  const removeEditImage = (image) => {
    if (!editingProperty) {
      return;
    }

    if (isDataImage(image)) {
      setEditingProperty((prev) => ({
        ...prev,
        uploaded_images: (prev.uploaded_images || []).filter((item) => item !== image),
      }));
      return;
    }

    const nextUrls = parseImageLines(editingProperty.image_urls_text).filter((item) => item !== image);
    setEditingProperty((prev) => ({
      ...prev,
      image_urls_text: nextUrls.join("\n"),
    }));
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-slate-950 font-sans text-white">
      <div className="absolute inset-0">
        <video data-workspace-background="true" aria-hidden="true"
          className="h-full w-full object-cover"
          src={bgVideo}
          autoPlay
          muted
          loop
          playsInline
        />
        <div className="absolute inset-0 bg-slate-950/85" />
      </div>

      <div className="workspace-page-body relative z-10 min-h-screen px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-col gap-4 border-b border-white/10 pb-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.35em] text-teal-300">
              Admin Properties
            </p>
            <h1 className="mt-2 text-3xl font-bold tracking-wide md:text-4xl">
              List Properties
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-300 md:text-base">
              View and update assigned tenants, occupancy state, and property details.
            </p>
          </div>

          <button
            className="ws-button"
            onClick={() => navigate("/add-property")}
          >
            Add property
          </button>
        </div>

        <div className="hidden overflow-x-auto rounded-lg border border-white/10 bg-slate-950/90 shadow-2xl md:block">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-white/10 bg-white/5 text-xs uppercase tracking-[0.18em] text-slate-400">
                <th className="px-4 py-3 font-semibold">Property</th>
                <th className="px-4 py-3 font-semibold">Location</th>
                <th className="px-4 py-3 font-semibold">Rent</th>
                <th className="px-4 py-3 font-semibold">Tenant</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Lease</th>
                <th className="px-4 py-3 font-semibold">Description</th>
                <th className="px-4 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {properties.map((p) => (
                <tr key={p.id} className="border-b border-white/10 transition hover:bg-white/5">
                  <td className="px-4 py-3 font-semibold">{p.property_name}</td>
                  <td className="px-4 py-3">{p.location}</td>
                  <td className="px-4 py-3">PHP {Number(p.rent_price || 0).toLocaleString()}</td>
                  <td className="px-4 py-3">{p.tenant ? p.tenant.name : "-"}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${
                        p.isOccupied
                          ? "border-teal-300/40 bg-teal-500/15 text-teal-200"
                          : "border-amber-300/40 bg-amber-500/15 text-amber-200"
                      }`}
                    >
                      {p.isOccupied ? "Occupied" : "Vacant"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="space-y-1">
                      <span
                        className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${
                          p.lease_health === "expired"
                            ? "border-red-300/40 bg-red-500/15 text-red-200"
                            : ["expiring_7", "expiring_15", "expiring_30"].includes(p.lease_health)
                            ? "border-amber-300/40 bg-amber-500/15 text-amber-200"
                            : "border-teal-300/40 bg-teal-500/15 text-teal-200"
                        }`}
                      >
                        {leaseHealthLabel(p.lease_health)}
                      </span>
                      <p className="text-xs text-slate-400">
                        {formatDate(p.lease_start_date)} - {formatDate(p.lease_end_date)}
                      </p>
                      <p className="text-xs text-slate-500">
                        {renewalLabel(p.renewal_status)}
                      </p>
                    </div>
                  </td>
                  <td className="max-w-[260px] px-4 py-3 text-slate-300">{p.description || "-"}</td>
                  <td className="px-4 py-3 text-right">
                    <ActionMenu context={p.property_name}><button
                        className="rounded-lg bg-teal-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-teal-500"
                        onClick={() =>
                          setEditingProperty({
                            ...p,
                            image_urls_text: (p.images || []).filter((image) => !isDataImage(image)).join("\n"),
                            uploaded_images: (p.images || []).filter((image) => isDataImage(image)),
                          })
                        }
                      >
                        Edit
                      </button>
                      <button data-danger="true"
                        onClick={() => handleDelete(p.id)}
                        className="rounded-lg border border-red-300/30 bg-red-500/15 px-3 py-2 text-sm font-semibold text-red-100 transition hover:bg-red-500/25"
                      >
                        Archive
                      </button></ActionMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="grid gap-4 md:hidden">
          {properties.map((p) => (
            <article
              key={p.id}
              className="rounded-lg border border-white/10 bg-slate-950/90 p-4 shadow-xl"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-bold text-white">{p.property_name}</h3>
                  <p className="mt-1 text-sm text-slate-300">{p.location}</p>
                </div>
                <span
                  className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${
                    p.isOccupied
                      ? "border-teal-300/40 bg-teal-500/15 text-teal-200"
                      : "border-amber-300/40 bg-amber-500/15 text-amber-200"
                  }`}
                >
                  {p.isOccupied ? "Occupied" : "Vacant"}
                </span>
              </div>

              <div className="mt-3 text-sm text-slate-300">
                <p>Rent: PHP {Number(p.rent_price || 0).toLocaleString()}</p>
                <p>Tenant: {p.tenant?.name || "-"}</p>
                <p>
                  Lease: {formatDate(p.lease_start_date)} - {formatDate(p.lease_end_date)}
                </p>
                <p className="text-xs text-slate-400">
                  {leaseHealthLabel(p.lease_health)} / {renewalLabel(p.renewal_status)}
                </p>
                <p className="mt-1 text-slate-400">{p.description || "-"}</p>
              </div>

              <ActionMenu context={p.property_name}><button
                  className="rounded-lg bg-teal-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-teal-500"
                  onClick={() =>
                    setEditingProperty({
                      ...p,
                      image_urls_text: (p.images || []).filter((image) => !isDataImage(image)).join("\n"),
                      uploaded_images: (p.images || []).filter((image) => isDataImage(image)),
                    })
                  }
                >
                  Edit
                </button>
                <button data-danger="true"
                  className="rounded-lg border border-red-300/30 bg-red-500/15 px-3 py-2 text-sm font-semibold text-red-100 transition hover:bg-red-500/25"
                  onClick={() => handleDelete(p.id)}
                >
                  Archive
                </button></ActionMenu>
            </article>
          ))}
        </div>

        {editingProperty && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
            <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg border border-white/10 bg-slate-950 p-6 shadow-2xl">
              <div className="mb-4 border-b border-white/10 pb-3">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-teal-300">
                  Edit Property
                </p>
                <h3 className="mt-1 text-xl font-bold text-white">
                  {editingProperty.property_name || "Property"}
                </h3>
              </div>

              <input
                name="property_name"
                value={editingProperty.property_name}
                onChange={handleEditChange}
                className="mb-3 w-full rounded-lg border border-white/10 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
              />
              <input
                name="location"
                value={editingProperty.location}
                onChange={handleEditChange}
                className="mb-3 w-full rounded-lg border border-white/10 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
              />
              <input
                name="rent_price"
                value={editingProperty.rent_price}
                onChange={handleEditChange}
                className="mb-3 w-full rounded-lg border border-white/10 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
              />

              <div className="mb-3 grid gap-3 sm:grid-cols-2">
                <input
                  type="date"
                  name="lease_start_date"
                  value={editingProperty.lease_start_date || ""}
                  onChange={handleEditChange}
                  className="w-full rounded-lg border border-white/10 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
                />
                <input
                  type="date"
                  name="lease_end_date"
                  value={editingProperty.lease_end_date || ""}
                  onChange={handleEditChange}
                  className="w-full rounded-lg border border-white/10 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
                />
              </div>

              <div className="mb-3 grid gap-3 sm:grid-cols-2">
                <input
                  type="date"
                  name="notice_date"
                  value={editingProperty.notice_date || ""}
                  onChange={handleEditChange}
                  className="w-full rounded-lg border border-white/10 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
                />
                <input
                  type="date"
                  name="move_out_date"
                  value={editingProperty.move_out_date || ""}
                  onChange={handleEditChange}
                  className="w-full rounded-lg border border-white/10 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
                />
              </div>

              <select
                name="renewal_status"
                value={editingProperty.renewal_status || "active"}
                onChange={handleEditChange}
                className="mb-3 w-full rounded-lg border border-white/10 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
              >
                <option value="active">Active</option>
                <option value="renewal_due">Renewal Due</option>
                <option value="renewed">Renewed</option>
                <option value="move_out_pending">Move-Out Pending</option>
                <option value="ended">Ended</option>
              </select>

              <label className="mb-3 flex items-center gap-3 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-slate-200">
                <input
                  type="checkbox"
                  name="isOccupied"
                  checked={editingProperty.isOccupied}
                  onChange={handleEditChange}
                />
                Occupied
              </label>

              {editingProperty.isOccupied && (
                <select
                  name="tenant_id"
                  value={editingProperty.tenant_id || ""}
                  onChange={handleEditChange}
                  className="mb-3 w-full rounded-lg border border-white/10 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
                >
                  <option value="">Select Tenant</option>
                  {tenants.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              )}

              <textarea
                name="description"
                value={editingProperty.description}
                onChange={handleEditChange}
                className="mb-4 w-full rounded-lg border border-white/10 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
                rows={3}
              />

              <textarea
                name="image_urls_text"
                value={editingProperty.image_urls_text || ""}
                onChange={handleEditChange}
                className="mb-4 w-full rounded-lg border border-white/10 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
                rows={3}
                placeholder="Apartment image URLs (one per line)"
              />

              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleEditImageUpload}
                className="mb-3 w-full rounded-lg border border-white/10 bg-white px-3 py-2 text-xs text-slate-900"
              />

              {mergeImageValues([
                ...(editingProperty.uploaded_images || []),
                ...parseImageLines(editingProperty.image_urls_text),
              ]).length > 0 && (
                <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {mergeImageValues([
                    ...(editingProperty.uploaded_images || []),
                    ...parseImageLines(editingProperty.image_urls_text),
                  ]).map((image) => (
                    <div key={image} className="relative overflow-hidden rounded-lg border border-white/20">
                      <img src={image} alt="Unit preview" className="h-20 w-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeEditImage(image)}
                        className="absolute right-1 top-1 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-semibold text-white"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <ListingDetailsFields value={editingProperty.listing_details || {}} onChange={(listing_details) => setEditingProperty((prev) => ({ ...prev, listing_details }))} />
              <textarea
                name="lease_notes"
                value={editingProperty.lease_notes || ""}
                onChange={handleEditChange}
                className="mb-4 w-full rounded-lg border border-white/10 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
                rows={2}
                placeholder="Lease notes"
              />

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setEditingProperty(null)}
                  className="rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-slate-200 transition hover:bg-white/10"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-teal-500"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}

        {toast && (
          <div
            className={`fixed right-4 top-4 z-50 rounded-lg px-5 py-3 text-sm font-semibold text-white shadow-2xl ${
              toast.type === "success" ? "bg-teal-600" : "bg-red-600"
            }`}
          >
            <div className="flex items-center gap-3">
              <span>{toast.message}</span>
              {undoPropertyId && (
                <button
                  onClick={handleUndoArchive}
                  className="rounded border border-white/30 px-2 py-1 text-xs font-semibold hover:bg-white/10"
                >
                  Undo
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
